import { OAuthProvider } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import logoGoogle from '../../../assets/images/google-logo.svg';
import logoQuickbooks from '../../../assets/images/logo-quickbooks.svg';
import logoSlack from '../../../assets/images/logo-slack.svg';
import logoXero from '../../../assets/images/logo-xero.svg';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Button } from '../../../design-system/button/button';
import { ColumnHeaderWithSorting } from '../../../design-system/data-table/column-header-with-sorting';
import { Inline } from '../../../design-system/inline/inline';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { OAuthConnectionStatus, OAuthProviderConnection } from '../types/types';

export const STATUS_COLUMN_ID = 'status';

const STATUS_ORDER: Record<OAuthConnectionStatus, number> = {
  CONNECTED: 0,
  DISCONNECTED: 1,
  COMING_SOON: 2,
};

const columnHelper = createColumnHelper<OAuthProviderConnection>();

export const oauthConnectionTableColumnsDefinition = [
  columnHelper.accessor('id', {
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Integration" />,
    cell: ({ row }) => {
      const { logo, name } = getCellContent(row.original.id);

      return <AvatarNameDescription avatar={{ showAvatar: true, avatarUrl: logo }} name={name} />;
    },
    size: 20,
  }),
  columnHelper.accessor(
    (row) => {
      const { description } = getCellContent(row.id);

      return description;
    },
    {
      id: 'description',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Description" />,
      cell: ({ row }) => {
        const { description } = getCellContent(row.original.id);

        return <p className="text-xs font-normal leading-6 tracking-tight text-seapoint-gray-text">{description}</p>;
      },
      size: 65,
    },
  ),
  columnHelper.accessor('status', {
    header: () => <span className="sr-only">Actions</span>,
    meta: {
      align: 'end',
    },
    cell: ({ row, table }) => {
      const { status } = row.original;

      switch (status) {
        case 'CONNECTED':
          return (
            <Inline justify="end">
              <Button
                variant="ghost"
                aria-label={`View ${row.original.id} connection details`}
                size="icon"
                onClick={() => table.options.meta?.onReviewInvoice?.(row.original.id)}
              >
                <div className="h-4 w-4 rounded-full">
                  <ChevronRight className="h-full w-full" />
                </div>
              </Button>
            </Inline>
          );
        case 'DISCONNECTED':
          return (
            <Inline justify="end">
              <Button variant="default" onClick={() => table.options.meta?.onReviewInvoice?.(row.original.id)}>
                Connect
              </Button>
            </Inline>
          );
        case 'COMING_SOON':
          return <p className="text-right text-xs font-medium leading-6 tracking-tight text-black">Coming soon</p>;
        default:
          return exhaustiveCheck(status);
      }
    },
    sortingFn: (rowA, rowB) => STATUS_ORDER[rowA.original.status] - STATUS_ORDER[rowB.original.status],
    size: 15,
    minSize: 15,
  }),
];

const getCellContent = (oauthProviderConnection: OAuthProviderConnection['id']) => {
  switch (oauthProviderConnection) {
    case OAuthProvider.XERO:
      return {
        logo: logoXero,
        name: 'Xero',
        description: 'Sync Seapoint with Xero to keep your books clean and your team in control.',
      };
    case OAuthProvider.GMAIL:
      return {
        logo: logoGoogle,
        name: 'Gmail',
        description: `We'll automatically fetch all invoices from your inbox, and help you manage which ones you've paid and which ones you need to pay.`,
      };
    case 'slack':
      return {
        logo: logoSlack,
        name: 'Slack',
        description: 'Communicate with your Seapoint account from Slack.',
      };
    case 'quickbooks':
      return {
        logo: logoQuickbooks,
        name: 'Quickbooks',
        description:
          'Push data from Seapoint to your Quickbooks account to keep your books tidy and your accountant happy.',
      };
    default:
      return exhaustiveCheck(oauthProviderConnection);
  }
};
